<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { getApiErrorMessage } from "@/helpers/error";
import interviewService from "@/modules/worker/services/interviewService";

import ArlyAiButton from "@/dsComponents/buttons/ArlyAiButton.vue";
import TextareaInputAlt from "@/dsComponents/inputs/TextareaInputAlt.vue";

interface InterviewTabProps {
    workerId: string;
}

const props = defineProps<InterviewTabProps>();
const content = defineModel<string>("content", { default: "" });
const { t } = useI18n();
const toaster = useToast();

const isLoadingSuggestions = ref(false);
const questionGenerationClickCount = ref(0);
const existingQuestions = ref<string[]>([]);

const suggestionsLabel = computed(() =>
    questionGenerationClickCount.value === 0
        ? t("page.interim.profile.interview.suggestions")
        : t("page.interim.profile.interview.suggestionsRegenerate"),
);

const handleGetSuggestions = async () => {
    isLoadingSuggestions.value = true;
    const nextClickNumber = questionGenerationClickCount.value + 1;
    try {
        const result = await interviewService.postInterviewAiSuggestions(props.workerId, {
            click_number: nextClickNumber,
            existing_questions: [...existingQuestions.value],
        });
        questionGenerationClickCount.value = nextClickNumber;
        existingQuestions.value.push(...result.questions);
        const block = result.questions.join("\n\n\n");
        const trimmed = content.value.trim();
        content.value = trimmed ? `${trimmed}\n\n${block}` : block;
    } catch (e) {
        console.error("Error fetching suggestions:", e);
        toaster?.open({ message: getApiErrorMessage(e), type: "error", duration: 5000 });
    } finally {
        isLoadingSuggestions.value = false;
    }
};
</script>
<template>
    <div class="interview-tab">
        <div class="suggestions-bar">
            <ArlyAiButton
                size="sm"
                icon="sparkles-fill"
                :label="suggestionsLabel"
                :loading="isLoadingSuggestions"
                @click="handleGetSuggestions"
            />
        </div>

        <TextareaInputAlt
            class="interview-tab__textarea"
            v-model="content"
            :rows="3"
            size="sm"
            no-border
            fill-parent-height
            textarea-font-weight="500"
            :placeholder="t('page.interim.profile.interview.interviewReportPlaceholder')"
        />
    </div>
</template>
<style scoped lang="scss">
.interview-tab {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    gap: var(--spacing-xl, 16px);
}

.suggestions-bar {
    display: flex;
    flex-shrink: 0;
    justify-content: flex-end;
}

.interview-tab__textarea {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
</style>
